import React from 'react';
import { IconButton, Box, Typography } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, Delete as DeleteIcon } from '@mui/icons-material';
import DataTable, { StatusColumn } from '../DataTable';
import { Product } from '../../../types';

export interface QuoteProductItem {
  id: string;
  product_id: string;
  product: Product;
  quantity: number;
  unit_price: number;
  [key: string]: unknown;
}

interface QuoteProductsTableProps {
  data: QuoteProductItem[];
  className?: string;
  onRemoveItem: (itemId: string) => void;
  onQuantityChange: (itemId: string, newQuantity: number) => void;
}

const QuoteProductsTable: React.FC<QuoteProductsTableProps> = ({
  data,
  className,
  onRemoveItem,
  onQuantityChange,
}) => {
  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const columns: StatusColumn[] = [
    {
      id: 'product',
      label: 'Produto',
      minWidth: 200,
      format: (value: unknown, row: QuoteProductItem) => (
        <Typography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '180px',
          }}
        >
          {row.product.name}
        </Typography>
      ),
    },
    {
      id: 'product_id',
      label: 'SKU',
      minWidth: 150,
      format: (value: unknown, row: QuoteProductItem) => row.product.sku,
    },
    {
      id: 'quantity',
      label: 'Quantidade',
      minWidth: 150,
      align: 'center',
      format: (value: unknown, row: QuoteProductItem) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            size="small"
            onClick={() => onQuantityChange(row.id, row.quantity - 1)}
            sx={{
              bgcolor: '#f3f3f3',
              border: '1px solid #d9d9d9',
              borderRadius: 0,
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: 30, textAlign: 'center', fontSize: 14, mx: 1 }}>{row.quantity}</Box>
          <IconButton
            size="small"
            onClick={() => onQuantityChange(row.id, row.quantity + 1)}
            sx={{
              bgcolor: '#f3f3f3',
              border: '1px solid #d9d9d9',
              borderRadius: 0,
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      id: 'unit_price',
      label: 'Preço Unitário (R$)',
      minWidth: 150,
      align: 'right',
      format: (value: unknown) => `R$ ${formatCurrency(value as number)}`,
    },
    {
      id: 'subtotal',
      label: 'Subtotal (R$)',
      minWidth: 150,
      align: 'right',
      format: (value: unknown, row: QuoteProductItem) =>
        `R$ ${formatCurrency(row.unit_price * row.quantity)}`,
    },
    {
      id: 'actions',
      label: 'Excluir item',
      minWidth: 120,
      align: 'center',
      format: (value: unknown, row: QuoteProductItem) => (
        <IconButton size="small" sx={{ color: '#505050' }} onClick={() => onRemoveItem(row.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      containerClassName={className}
      fullWidth
      rounded
      emptyMessage="Nenhum produto adicionado. Use a busca acima para adicionar produtos."
    />
  );
};

export default QuoteProductsTable;
